import { FaceRecognitionStatusEnum, LayoutEnum } from '@goldcast/api/content';
import { Clip } from '@goldcast/player-config/src/types/clip-types';
import { getLayoutAvailabilityStatusPure } from '@goldcast/player-config/src/utils/getLayoutAvailabilityStatusPure';
import { isClipPreRecordedEvent } from '@goldcast/player-config/src/utils/clipUtils';
import { isFullVideoFaceRecEnabled, isFullVideoFaceRecForPreRecordedEnabled } from '@/stores/clip';
import { getErrorDiagnostics } from '@/libs/errorDiagnostics';

export function getLayoutAvailabilityStatus(
  clipLayout: LayoutEnum,
  layoutStatus: Clip['layout_status'],
  clip: Clip,
  useNewPlayer: boolean = false
): FaceRecognitionStatusEnum | null {
  return getLayoutAvailabilityStatusPure({
    clip,
    clipLayout,
    layoutStatus,
    useNewPlayer,
    isFullFaceRecEnabled: isFullVideoFaceRecEnabled(),
    isFullFaceRecForPreRecordedEnabled: isFullVideoFaceRecForPreRecordedEnabled()
  });
}

export const SCREENSHARE_TOAST_MESSAGE =
  'Screenshare detected. Update track selection in scenes to show or hide the screenshare stream.';

export function logClipsContextError(
  logger: Pick<Window['logger'], 'error'>,
  operation: 'fetch clip' | 'initialize clip content',
  clipId: string | undefined,
  error: unknown
): void {
  logger.error(`[ClipsContext] Failed to ${operation}`, {
    clipId,
    ...getErrorDiagnostics(error)
  });
}

export function hasShownScreenshareToast(clipId: string): boolean {
  const storageKey = `screenshareDetectionToastShown_${clipId}`;

  try {
    return localStorage.getItem(storageKey) === 'true';
  } catch (error) {
    window.logger?.error('[ClipsContextUtils] Failed to read screenshare toast state from localStorage', {
      clipId,
      ...getErrorDiagnostics(error)
    });
    return false;
  }
}

export function markScreenshareToastAsShown(clipId: string): void {
  const storageKey = `screenshareDetectionToastShown_${clipId}`;

  try {
    localStorage.setItem(storageKey, 'true');
  } catch (error) {
    window.logger?.error('[ClipsContextUtils] Failed to persist screenshare toast state to localStorage', {
      clipId,
      ...getErrorDiagnostics(error)
    });
  }
}

export interface VisionRecognitionEvent {
  /** Encoded state, e.g. "FULLVIDEO:DONE" or "ACCURATE:PROCESSING". */
  state: string;
  /** Per-clip events identify the clip by its id. */
  id?: string;
  upload_id?: string;
  broadcast_id?: string;
}

export interface ParsedVisionEvent {
  status: FaceRecognitionStatusEnum;
  isFullVideo: boolean;
  isAnalysisAccurate: boolean;
}

export function parseVisionRecognitionEvent(data: VisionRecognitionEvent): ParsedVisionEvent {
  const status = data.state.split(':')[1] as FaceRecognitionStatusEnum;
  const isFullVideo = data.state.includes('FULLVIDEO');

  // Full-video has a single (accurate) flow — there is no fast/accurate split.
  const isAnalysisAccurate = isFullVideo || data.state.includes('ACCURATE');

  return { status, isFullVideo, isAnalysisAccurate };
}

/**
 * Whether a facial-recognition event should be processed for this clip.
 * The backend emits FULLVIDEO events unconditionally; this guard ignores them when
 * the full-video flag is off for the clip (those clips rely on the per-clip ACCURATE flow).
 * Uploads and pre-recorded events each have their own flag, so both are considered.
 */
export function shouldProcessVisionEvent({
  clip,
  isFullVideo,
  isFullVideoFaceRecEnabled,
  isFullVideoFaceRecForPreRecordedEnabled
}: {
  clip: Clip;
  isFullVideo: boolean;
  isFullVideoFaceRecEnabled: boolean;
  isFullVideoFaceRecForPreRecordedEnabled: boolean;
}): boolean {
  const isFlagEnabled = isClipPreRecordedEvent(clip)
    ? isFullVideoFaceRecForPreRecordedEnabled
    : isFullVideoFaceRecEnabled;
  return !(isFullVideo && !isFlagEnabled);
}
